import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import { getWard } from "../../services/ValitanApiService";
import Select from "react-select";
import {
  // confirmDelete,
  createOrUpdateWard,
  getWards as fetchWards,
  mergeWardsApi
} from "../Wards/WardService";
import { getDistricts as fetchDistricts } from "../Districts/DistrictService";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ConfirmDialog } from "primereact/confirmdialog";
import WardUploadModal from "../Wards/WardsUploadModal";
import { enqueueSnackbar } from "notistack";

const WardListTables = () => {
  document.title = "Wards | Valitan IMS";

  const [wards, setWards] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false); // State for upload modal
  const [wardName, setWardName] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({ wardName: "", district: "" });
  const [editingWardId, setEditingWardId] = useState(null);
  
  const [selectedWardsForMerge, setSelectedWardsForMerge] = useState(new Set());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [targetWardId, setTargetWardId] = useState("");
  const [isMerging, setIsMerging] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  const [selectedFile, setSelectedFile] = useState(null); // State to hold selected file
  useEffect(() => {
    // Fetch regions once when component mounts (or re-fetch only if necessary)
    loadDistricts();
  }, []); // Empty dependency array ensures this only runs on mount

  useEffect(() => {
    // Fetch districts every time pagination info or page size changes
    loadWards(0, paginationInfo.pageSize);
  }, [paginationInfo.pageSize]);

  const loadWards = async (page = 0, size = 10) => {
    const { wards, paginationInfo } = await fetchWards(page, size);
    setWards(wards);
    setPaginationInfo(paginationInfo);
  };

  const loadDistricts = async () => {
    try {
      const response = await fetchDistricts();
      const districtOptions = response.districts.map((district) => ({
        value: district.id,
        label: district.name,
      }));
      setDistricts(districtOptions);
    } catch (error) {
      console.error("Error fetching districts:", error);
      enqueueSnackbar("Error fetching districts:", {variant:"error"})
      setDistricts([]); // Default to an empty array to avoid errors
    }
  };

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadWards(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadWards(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const openModalForEdit = async (id) => {
    setEditingWardId(id);
    try {
      const response = await getWard(id);
      if (response) {
        const field = response.data.data;
        setWardName(field.name);
        setSelectedDistrict({
          value: field.district.id,
          label: field.district.name,
        });
        setModalOpen(true);
      } else {
        console.error("Ward data is undefined");
      }
    } catch (error) {
      console.error("Error fetching Ward data:", error);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      setWardName("");
      setSelectedDistrict(null);
      setErrors({
        wardName: "",
        district: "",
      });
      setEditingWardId(null);
    }
  };

  const handleSelectChange = (selectedOption) => {
    setSelectedDistrict(selectedOption);
    setErrors((prevErrors) => ({ ...prevErrors, district: "" })); // Clear the error when a district is selected
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    createOrUpdateWard(
      event,
      wardName, // Pass wardName directly
      selectedDistrict, // Pass selectedDistrict directly
      editingWardId,
      loadWards,
      toggleModal,
      setErrors
    );
  };


  const toggleUploadModal = () => {
    setUploadModalOpen(!uploadModalOpen);
    setSelectedFile(selectedFile); // Clear selected file when closing modal
  };

  const handleSelectWard = (wardId) => {
    setSelectedWardsForMerge(prev => {
      const next = new Set(prev);
      if (next.has(wardId)) {
        next.delete(wardId);
      } else {
        next.add(wardId);
      }
      return next;
    });
  };

  const handleSelectAllWards = (e) => {
    if (e.target.checked) {
      const allIds = new Set(wards.map(w => w.id));
      setSelectedWardsForMerge(allIds);
    } else {
      setSelectedWardsForMerge(new Set());
    }
  };

  const toggleMergeModal = () => {
    setMergeModalOpen(!mergeModalOpen);
    if (!mergeModalOpen) {
      setTargetWardId("");
    }
  };

  const handleMerge = async () => {
    if (!targetWardId) {
      enqueueSnackbar("Please select a target ward.", { variant: "warning" });
      return;
    }
    const sourceIds = Array.from(selectedWardsForMerge);
    if (sourceIds.length === 0) return;

    setIsMerging(true);
    const success = await mergeWardsApi(sourceIds, targetWardId);
    setIsMerging(false);

    if (success) {
      toggleMergeModal();
      setSelectedWardsForMerge(new Set());
      loadWards(paginationInfo.pageNumber, paginationInfo.pageSize);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Wards setup" breadcrumbItem="Wards" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      color="primary"
                      className="add-btn float-end ms-2"
                      id="TooltipTop"
                      onClick={toggleUploadModal}
                    >
                      <i className="ri-upload-cloud-2-line align-bottom me-1"></i> Upload wards
                    </Button>
                    <Button
                      color="warning"
                      className="add-btn float-end ms-2"
                      disabled={selectedWardsForMerge.size === 0}
                      onClick={toggleMergeModal}
                    >
                      <i className="ri-merge-cells-horizontal align-bottom me-1"></i> Merge Selected ({selectedWardsForMerge.size})
                    </Button>
                    {/* <Button
                      color="primary"
                      className="add-btn float-end"
                      id="TooltipTop"
                      onClick={toggleModal}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Add
                    </Button> */}
                  </h4>
                </CardHeader>

                <CardBody>
                  <div>
                    <Row className="g-4 mb-3">
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search..."
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table
                        className="table align-middle table-nowrap"
                        id="customerTable"
                      >
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "50px" }}>
                              <Input
                                type="checkbox"
                                checked={wards.length > 0 && selectedWardsForMerge.size === wards.length}
                                onChange={handleSelectAllWards}
                              />
                            </th>
                            <th className="sort" data-sort="email">
                              #
                            </th>
                            <th className="sort" data-sort="email">
                              Ward
                            </th>
                            <th className="sort" data-sort="email">
                              Council
                            </th>
                            <th className="sort" data-sort="action">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {wards.length > 0 ? (
                            wards.map((ward, index) => (
                              <tr key={ward.id}>
                                <td>
                                  <Input
                                    type="checkbox"
                                    checked={selectedWardsForMerge.has(ward.id)}
                                    onChange={() => handleSelectWard(ward.id)}
                                  />
                                </td>
                                <td>{startIndex + index}</td>
                                <td>{ward.name}</td>
                                <td>{ward.council?.name || "N/A"}</td> 
                                <td>
                                  <div
                                    className="btn-group"
                                    role="group"
                                    aria-label="Basic example"
                                  >
                                    <button
                                      id="TooltipTop"
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={() =>
                                        openModalForEdit(ward.id)
                                      }
                                    >
                                      <i className="ri-edit-box-line"></i>
                                    </button>
                                    {/* <button
                                      type="button"
                                      onClick={() =>
                                        confirmDelete(ward.id, loadWards)
                                      }
                                      className="btn btn-danger btn-sm"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button> */}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: "center" }}>
                                No records found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      paginationInfo={paginationInfo}
                      handleNextPage={handleNextPage}
                      handlePreviousPage={handlePreviousPage}
                      onPageChange={(pageNumber, pageSize) => loadWards(pageNumber, pageSize)}
                      onPageSizeChange={(newPageSize) => {
                        setPaginationInfo((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                          pageNumber: 0, // Reset to the first page
                        }));
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmDialog />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop={"static"}>
        <ModalHeader toggle={toggleModal}>
          {editingWardId ? "Edit ward" : "Add ward"}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>

            <FormGroup>
              <Label for="wardName">Name</Label>
              <Input
                type="text"
                name="wardName"
                id="wardName"
                placeholder="Enter Name"
                value={wardName}
                onChange={(e) => setWardName(e.target.value)}
                invalid={!!errors.wardName}
              />
              {errors.wardName && (
                <div className="invalid-feedback">{errors.wardName}</div>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="district">District</Label>
              <Select
                name="district"
                id="district"
                placeholder="Select a District"
                value={selectedDistrict}
                onChange={handleSelectChange}
                options={districts || []}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: errors.district ? '#dc3545' : base.borderColor,
                    '&:hover': {
                      borderColor: errors.district ? '#dc3545' : base['&:hover'].borderColor,
                    },
                  }),
                }}
              />
              {errors.district && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {errors.district}
                </div>
              )}
            </FormGroup>
            <Button type="submit" color="primary" className="float-end">
              {editingWardId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>

      <
        WardUploadModal
        isOpen={uploadModalOpen}
        toggle={toggleUploadModal}
        loadWards={loadWards}
        paginationInfo={paginationInfo}
      />
      <Modal isOpen={mergeModalOpen} toggle={toggleMergeModal} centered>
        <ModalHeader toggle={toggleMergeModal}>Merge Wards</ModalHeader>
        <ModalBody>
          <p>
            You are about to merge <strong>{selectedWardsForMerge.size}</strong> selected ward(s) into one target ward.
            This will re-assign all associated assets and institution configs to the target ward, and delete the source wards.
          </p>
          <FormGroup>
            <Label className="fw-semibold">Select Target Ward</Label>
            <Select
              options={wards.map(w => ({ value: w.id, label: `${w.name} ${w.council?.name ? `(${w.council.name})` : ""}` }))}
              value={targetWardId ? { value: targetWardId, label: wards.find(w => w.id === targetWardId)?.name || "Selected" } : null}
              onChange={(opt) => setTargetWardId(opt ? opt.value : "")}
              placeholder="Search target ward..."
              isClearable
              isSearchable
            />
          </FormGroup>
          <div className="text-end mt-4">
            <Button color="light" onClick={toggleMergeModal} className="me-2" disabled={isMerging}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleMerge} disabled={isMerging || !targetWardId}>
              {isMerging ? "Merging..." : "Confirm Merge"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default WardListTables;
